/* eslint-disable react/no-unescaped-entities */
/**
 * ExpertDetailBooking — Fiche spécialiste Premium style "Booking".
 *
 * Vitrine professionnelle pour les cabinets référencés Confilex (`unlocked=true`).
 * Pour les profils anonymisés non débloqués, redirige vers /réseau de spécialistes-avocats/:slug
 * qui contient le mur de paiement Stripe (29 € / 39 € / 49 €).
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import BookingNavbar from "@/components/booking/BookingNavbar";
import Footer from "@/components/Footer";
import Seo, { ldBreadcrumb } from "@/components/Seo";
import {
  MapPin, Phone, Mail, Globe, ShieldCheck, Crown, Sparkles, ArrowRight,
  Building2, Languages, Video, Clock, Star, Calendar, FileText,
  ChevronLeft, Award, Send, MessageCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExpertDetailBooking() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    axios
      .get(`${API}/lawyers-directory/${slug}`)
      .then((r) => {
        setData(r.data);
        // Si profil anonyme non-débloqué, on redirige vers la page paywall
        if (r.data?.locked && !r.data?.unlocked) {
          nav(`/annuaire-avocats/${slug}`, { replace: true });
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug, nav]);

  if (loading) return <LoadingShell />;
  if (error || !data) return <NotFoundShell slug={slug} />;
  if (data.locked) return <LoadingShell />; // pendant la redirection

  const name = data.cabinet || data.fullName || "Cabinet Confilex";
  const phone = data.phone;
  const email = data.email;
  const website = data.website;
  const addr = data.address1 || data.address;
  const mapsQuery = encodeURIComponent([addr, data.postal_code, data.city].filter(Boolean).join(", "));
  const isPremium = data.unlocked_tier === "premium" || data.featured;
  const isSubscribed = !!data.is_subscribed;
  const isReferenced = !isSubscribed;

  return (
    <div className="cb-shell" data-testid="expert-detail-booking">
      <Seo
        title={`${name} — ${data.city || "Paris"} · Confilex`}
        description={`${name} · Avocat au barreau de ${data.barreau} · Spécialités : ${(data.specialties || []).slice(0, 3).join(", ")}. Fiche vérifiée et structurée par Confilex.`}
        canonical={typeof window !== "undefined" ? window.location.origin + `/expert/${slug}` : undefined}
        jsonLd={[ldBreadcrumb([
          { name: "Accueil", url: "/" },
          { name: "Recherche", url: "/recherche" },
          { name },
        ])]}
      />

      <BookingNavbar />

      {/* Fil d'ariane */}
      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-6">
        <Link
          to="/recherche"
          className="inline-flex items-center gap-1.5 text-xs text-[#BFC8C2] hover:cb-gold transition-colors"
          data-testid="back-to-results"
        >
          <ChevronLeft className="w-4 h-4" /> Retour aux résultats
        </Link>
      </div>

      {/* HERO de la fiche */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-6 pb-10">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Colonne identité */}
          <div className="lg:col-span-8">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {isSubscribed && (
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold px-3 py-1 rounded-full"
                      style={{ background: "rgba(46,204,113,0.18)", color: "#2ECC71", border: "1px solid rgba(46,204,113,0.40)" }}
                      data-testid="badge-subscribed">
                  <ShieldCheck className="w-3 h-3" /> Spécialiste abonné Confilex
                </span>
              )}
              {isReferenced && (
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold px-3 py-1 rounded-full text-[#BFC8C2] border border-[#063D25] bg-[#031B12]"
                      data-testid="badge-referenced">
                  Professionnel référencé
                </span>
              )}
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold px-3 py-1 rounded-full"
                    style={{ background: "rgba(46,204,113,0.15)", color: "#2ECC71", border: "1px solid rgba(46,204,113,0.30)" }}>
                <ShieldCheck className="w-3 h-3" /> Vérifié CNB
              </span>
            </div>

            <h1 className="cb-serif text-4xl lg:text-5xl font-semibold cb-text-ivory leading-tight mb-3">
              {name}
            </h1>
            <p className="text-base lg:text-lg cb-gold font-medium mb-5">
              Avocat au Barreau de {data.barreau}
            </p>

            {/* Méta principale */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#BFC8C2] mb-7">
              {(addr || data.city) && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 cb-gold" />
                  {[addr, data.postal_code, data.city].filter(Boolean).join(", ")}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5">
                <Clock className="w-4 h-4 cb-gold" /> Réponse sous 2h ouvrables
              </span>
            </div>

            {/* Bandeau actions desktop */}
            <div className="hidden md:flex flex-wrap gap-3">
              <a
                href={`/dossier?expert=${data.slug}`}
                className="cb-btn-gold inline-flex items-center gap-2"
                data-testid="cta-transmit-dossier"
              >
                Transmettre mon dossier <Send className="w-4 h-4" />
              </a>
              <a
                href={`/dossier?expert=${data.slug}&action=rdv`}
                className="cb-btn-ghost inline-flex items-center gap-2"
                data-testid="cta-book-rdv"
              >
                <Calendar className="w-4 h-4" /> Prendre rendez-vous
              </a>
              {phone && (
                <a
                  href={`tel:${phone.replace(/\s/g, "")}`}
                  className="cb-btn-ghost inline-flex items-center gap-2"
                  data-testid="cta-call"
                >
                  <Phone className="w-4 h-4" /> {phone}
                </a>
              )}
            </div>
          </div>

          {/* Carte sticky desktop : coordonnées + CTA */}
          <aside className="lg:col-span-4 lg:sticky lg:top-28">
            <div className="cb-card p-6 space-y-5">
              <div className="space-y-3 text-sm">
                {phone && (
                  <a href={`tel:${phone.replace(/\s/g, "")}`}
                     className="flex items-center gap-3 cb-text-ivory hover:cb-gold transition-colors"
                     data-testid="contact-phone">
                    <Phone className="w-4 h-4 cb-gold" strokeWidth={1.6} /> {phone}
                  </a>
                )}
                {email && (
                  <a href={`mailto:${email}`}
                     className="flex items-center gap-3 cb-text-ivory hover:cb-gold transition-colors break-all"
                     data-testid="contact-email">
                    <Mail className="w-4 h-4 cb-gold" strokeWidth={1.6} /> {email}
                  </a>
                )}
                {website && (
                  <a href={website.startsWith("http") ? website : `https://${website}`}
                     target="_blank" rel="noopener noreferrer"
                     className="flex items-center gap-3 cb-text-ivory hover:cb-gold transition-colors"
                     data-testid="contact-website">
                    <Globe className="w-4 h-4 cb-gold" strokeWidth={1.6} /> {website}
                  </a>
                )}
                {addr && (
                  <div className="flex items-start gap-3 cb-text-ivory">
                    <MapPin className="w-4 h-4 cb-gold mt-0.5 shrink-0" strokeWidth={1.6} />
                    <span>
                      {addr}<br />
                      {data.postal_code} {data.city}
                    </span>
                  </div>
                )}
              </div>

              <div className="pt-4 border-t border-[#063D25] space-y-2">
                <a
                  href={`/dossier?expert=${data.slug}`}
                  className="cb-btn-gold w-full inline-flex items-center justify-center gap-2"
                  data-testid="sidebar-transmit-dossier"
                >
                  Transmettre mon dossier <ArrowRight className="w-4 h-4" />
                </a>
                <a
                  href={`/dossier?expert=${data.slug}&action=rdv`}
                  className="cb-btn-ghost w-full inline-flex items-center justify-center gap-2 text-sm"
                  data-testid="sidebar-book-rdv"
                >
                  <Calendar className="w-4 h-4" /> Prendre rendez-vous
                </a>
              </div>

              {/* Carte interactive Google Maps */}
              {addr && (
                <div className="pt-4 border-t border-[#063D25]">
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${mapsQuery}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block rounded-lg overflow-hidden border border-[#063D25] hover:border-[#F5C400]/40 transition-colors group relative aspect-video"
                    data-testid="map-link"
                  >
                    <img
                      src={`https://staticmap.openstreetmap.de/staticmap.php?center=48.8566,2.3522&zoom=12&size=600x300&markers=48.8566,2.3522,red`}
                      alt={`Localisation ${name}`}
                      loading="lazy"
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-[#031B12]/40 group-hover:bg-[#031B12]/20 transition-colors">
                      <span className="cb-btn-ghost text-xs">Voir sur la carte</span>
                    </div>
                  </a>
                </div>
              )}
            </div>
          </aside>
        </div>
      </section>

      {/* SECTIONS DÉTAIL */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-20">
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-6">

            {/* Présentation */}
            <Card>
              <CardTitle icon={Sparkles}>Présentation</CardTitle>
              <p className="text-[#BFC8C2] leading-relaxed">
                <strong className="cb-text-ivory">{name}</strong> est référencé au
                <strong className="cb-text-ivory"> Barreau de {data.barreau}</strong>. Sa fiche a été
                vérifiée et structurée par Confilex à partir de sources publiques officielles
                (registre CNB · Conseil National des Barreaux). Ce professionnel accompagne particuliers,
                dirigeants et entreprises sur des dossiers complexes nécessitant une expertise
                pointue.
              </p>
              {data.experience_years && (
                <p className="text-sm text-[#BFC8C2] mt-3">
                  <Award className="w-4 h-4 inline-block mr-1 cb-gold" />
                  <strong className="cb-text-ivory">{data.experience_years} ans</strong> d'expérience au Barreau.
                </p>
              )}
            </Card>

            {/* Domaines d'intervention */}
            <Card>
              <CardTitle icon={Building2}>Domaines d'intervention</CardTitle>
              <div className="flex flex-wrap gap-2">
                {(data.practice_areas || data.specialties || []).map((s) => (
                  <span key={s}
                        className="text-sm px-3 py-1.5 rounded-lg cb-bg-surface-2 cb-text-ivory border border-[#063D25]">
                    {s}
                  </span>
                ))}
              </div>
            </Card>

            {/* Modes de consultation */}
            <Card>
              <CardTitle icon={MessageCircle}>Modes de consultation</CardTitle>
              <ul className="grid sm:grid-cols-2 gap-3 text-sm">
                {[
                  { i: Building2, t: "Consultation au cabinet" },
                  { i: Video, t: "Consultation en visio" },
                  { i: Phone, t: "Entretien téléphonique" },
                  { i: FileText, t: "Dossier IA Confilex accepté" },
                ].map((row) => (
                  <li key={row.t} className="flex items-center gap-3 cb-text-ivory">
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ background: "rgba(245,196,0,0.10)", border: "1px solid rgba(245,196,0,0.25)" }}>
                      <row.i className="w-4 h-4 cb-gold" strokeWidth={1.6} />
                    </span>
                    {row.t}
                  </li>
                ))}
              </ul>
            </Card>

            {/* Langues */}
            <Card>
              <CardTitle icon={Languages}>Langues parlées</CardTitle>
              <div className="flex flex-wrap gap-2">
                {(data.languages || ["Français"]).map((l) => (
                  <span key={l} className="text-sm px-3 py-1.5 rounded-full cb-bg-surface border border-[#063D25] cb-text-ivory">
                    {l}
                  </span>
                ))}
              </div>
            </Card>

            {/* Documents acceptés / Process Confilex */}
            <Card>
              <CardTitle icon={FileText}>Process Confilex — Recevez un dossier qualifié</CardTitle>
              <ol className="space-y-3">
                {[
                  "Vous décrivez votre situation en quelques mots",
                  "Vous téléversez vos documents (contrats, correspondances, pièces)",
                  "L'IA Confilex structure : chronologie · résumé · pièces manquantes · points de vigilance",
                  `Le dossier est transmis à ${name}`,
                  "Échanges sécurisés depuis votre espace personnel",
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm cb-text-ivory">
                    <span className="w-6 h-6 rounded-full bg-[#F5C400] text-[#031B12] font-bold text-xs flex items-center justify-center shrink-0">
                      {i + 1}
                    </span>
                    {step}
                  </li>
                ))}
              </ol>
            </Card>
          </div>

          {/* Colonne droite : engagement Confilex */}
          <div className="lg:col-span-4 space-y-5">
            <Card>
              <CardTitle icon={ShieldCheck}>Engagement Confilex</CardTitle>
              <ul className="space-y-3 text-sm cb-text-ivory">
                {[
                  "Vérification CNB systématique",
                  "Aucun retour commercial dissimulé",
                  "Données sources : registre officiel public",
                  "Échanges chiffrés de bout en bout",
                  "Zero retention sur les analyses IA",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <ShieldCheck className="w-4 h-4 cb-gold shrink-0 mt-0.5" />
                    {t}
                  </li>
                ))}
              </ul>
            </Card>

            <Card>
              <CardTitle icon={Sparkles}>
                {isSubscribed ? "Cabinet abonné Confilex" : "Fiche issue de sources publiques"}
              </CardTitle>
              {isSubscribed ? (
                <p className="text-sm text-[#BFC8C2] leading-relaxed">
                  Ce cabinet est abonné à Confilex et a souscrit une offre. Il reçoit en priorité
                  les dossiers structurés par notre IA, dispose d'une fiche enrichie, d'un canal de
                  messagerie sécurisé et de fonctionnalités Premium.
                </p>
              ) : (
                <>
                  <p className="text-sm text-[#BFC8C2] leading-relaxed">
                    Les informations affichées proviennent de <strong className="cb-text-ivory">sources publiques</strong>
                    (site officiel du cabinet, registre CNB). Ce professionnel n'est <strong className="cb-text-ivory">pas
                    nécessairement abonné</strong> à Confilex.
                  </p>
                  <a
                    href="/expert/apply"
                    className="cb-btn-ghost w-full mt-4 text-xs inline-flex items-center justify-center"
                    data-testid="invite-cabinet"
                  >
                    Inviter ce professionnel à rejoindre Confilex
                  </a>
                </>
              )}
            </Card>
          </div>
        </div>
      </section>

      {/* CTA sticky mobile */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#031B12]/95 backdrop-blur-md border-t border-[#063D25] p-3 flex gap-2"
           data-testid="mobile-sticky-cta">
        <a
          href={`/dossier?expert=${data.slug}`}
          className="cb-btn-gold flex-1 text-center text-sm inline-flex items-center justify-center gap-2"
          data-testid="mobile-transmit-dossier"
        >
          Transmettre mon dossier
        </a>
        {phone && (
          <a
            href={`tel:${phone.replace(/\s/g, "")}`}
            className="cb-btn-ghost text-sm inline-flex items-center justify-center px-3"
            aria-label="Appeler"
            data-testid="mobile-call"
          >
            <Phone className="w-4 h-4" />
          </a>
        )}
      </div>

      <Footer />
    </div>
  );
}

function Card({ children }) {
  return <div className="cb-card p-6 lg:p-7">{children}</div>;
}

function CardTitle({ icon: Icon, children }) {
  return (
    <h2 className="cb-serif text-xl font-semibold cb-text-ivory mb-4 flex items-center gap-2">
      <Icon className="w-5 h-5 cb-gold" strokeWidth={1.6} />
      {children}
    </h2>
  );
}

function LoadingShell() {
  return (
    <div className="cb-shell">
      <BookingNavbar />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 text-center">
        <Sparkles className="w-8 h-8 cb-gold animate-pulse mx-auto" />
        <p className="mt-4 text-sm text-[#BFC8C2]">Chargement de la fiche spécialiste…</p>
      </div>
    </div>
  );
}

function NotFoundShell({ slug }) {
  return (
    <div className="cb-shell">
      <BookingNavbar />
      <div className="max-w-3xl mx-auto px-5 lg:px-8 py-20 text-center">
        <h1 className="cb-serif text-3xl cb-text-ivory">Fiche introuvable</h1>
        <p className="text-sm text-[#BFC8C2] mt-3">
          Nous ne trouvons pas le spécialiste <code className="cb-gold">{slug}</code>. Il a peut-être été déréférencé.
        </p>
        <Link to="/recherche" className="cb-btn-gold inline-block mt-6 text-sm">
          Retour à la recherche
        </Link>
      </div>
      <Footer />
    </div>
  );
}
